import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import SupplierDetailModal from "./components/SupplierDetailModal";
import SupplierProductAddModal from "./components/SupplierProductAddModal";
import { useDealerLog, LOG_ACTIONS } from "@/hooks/useDealerLog";

interface Supplier {
  id: string;
  name: string;
  phone: string;
  email: string;
}

type ViewMode = "grid" | "list";

export default function SuppliersPage() {
  const { user } = useAuth();
  const { logAction } = useDealerLog();
  const dealerId = user?.dealerId || user?.id || "";

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Supplier | null>(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [search, setSearch] = useState("");
  const [detailSupplier, setDetailSupplier] = useState<Supplier | null>(null);
  const [showProductAdd, setShowProductAdd] = useState(false);

  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ success: boolean; inserted: number; message: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchSuppliers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data, error: fetchErr } = await supabase
        .from("suppliers")
        .select("id, name, phone, email")
        .eq("dealer_id", dealerId)
        .order("name");
      if (fetchErr) throw new Error(fetchErr.message);
      setSuppliers(data || []);
    } catch (err: any) {
      setError(err.message || "Tedarikçiler yüklenirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, [dealerId]);

  useEffect(() => {
    if (dealerId) fetchSuppliers();
  }, [dealerId, fetchSuppliers]);

  const openAdd = () => { setEditing(null); setForm({ name: "", phone: "", email: "" }); setShowModal(true); };
  const openEdit = (s: Supplier) => { setEditing(s); setForm({ name: s.name, phone: s.phone || "", email: s.email || "" }); setShowModal(true); };

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), phone: form.phone.trim(), email: form.email.trim() };
      if (editing) {
        const { error: updateErr } = await supabase.from("suppliers").update(payload).eq("id", editing.id);
        if (updateErr) throw new Error(updateErr.message);
        logAction(dealerId, user?.id || null, LOG_ACTIONS.SUPPLIER_UPDATE, `"${form.name.trim()}" tedarikçisi güncellendi`);
      } else {
        const { error: insertErr } = await supabase.from("suppliers").insert({ ...payload, dealer_id: dealerId });
        if (insertErr) throw new Error(insertErr.message);
        logAction(dealerId, user?.id || null, LOG_ACTIONS.SUPPLIER_ADD, `"${form.name.trim()}" tedarikçisi eklendi`);
      }
      setShowModal(false);
      fetchSuppliers();
    } catch (err: any) {
      setError(err.message || "Kayıt sırasında hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  const deleteSupplier = async (id: string, name: string) => {
    if (!confirm("Bu tedarikçiyi silmek istediğinize emin misiniz?")) return;
    try {
      const { error: delErr } = await supabase.from("suppliers").delete().eq("id", id);
      if (delErr) throw new Error(delErr.message);
      logAction(dealerId, user?.id || null, LOG_ACTIONS.SUPPLIER_DELETE, `"${name}" tedarikçisi silindi`);
      fetchSuppliers();
    } catch (err: any) {
      setError(err.message || "Silme sırasında hata oluştu.");
    }
  };

  const handleExcelImportClick = () => {
    if (!dealerId) return;
    if (!confirm("Excel'den tedarikçi içe aktarmak istediğinize emin misiniz? Mevcut tedarikçiler değiştirilmeyecek, yeni kayıtlar eklenecek.")) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !dealerId) return;
    setImporting(true);
    setImportResult(null);

    try {
      const fileName = `suppliers_import_${Date.now()}_${file.name}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("public")
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw new Error(uploadError.message);

      const { data: publicUrlData } = supabase.storage.from("public").getPublicUrl(uploadData.path);
      const fileUrl = publicUrlData.publicUrl;

      const { data, error: fnError } = await supabase.functions.invoke("import-excel-suppliers", {
        body: { fileUrl, dealerId },
      });

      if (fnError) throw new Error(fnError.message || "İçe aktarma başarısız");
      if (!data || data.error) throw new Error(data?.error || "İçe aktarma başarısız");

      setImportResult({ success: true, inserted: data.inserted, message: `${data.inserted} tedarikçi başarıyla eklendi.` });
      logAction(dealerId, user?.id || null, LOG_ACTIONS.IMPORT, `Excel'den ${data.inserted} tedarikçi içe aktarıldı`);
      fetchSuppliers();
    } catch (err: any) {
      setImportResult({ success: false, inserted: 0, message: err.message || "İçe aktarma sırasında hata oluştu." });
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const filtered = suppliers.filter(
    (s) =>
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      (s.phone || "").includes(search) ||
      (s.email || "").toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto text-center py-16 text-foreground-400">
        <i className="ri-loader-4-line animate-spin text-3xl block mb-2"></i>
        <p className="text-sm">Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      {error && (
        <div className="bg-accent-100 border border-accent-300 text-accent-900 rounded-xl px-4 py-3 mb-4 text-sm flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {error}
          <button onClick={() => setError("")} className="ml-auto text-accent-700 font-medium cursor-pointer">Kapat</button>
        </div>
      )}

      {importResult && (
        <div className={`rounded-xl px-4 py-3 mb-4 text-sm flex items-center gap-2 ${importResult.success ? "bg-emerald-100 border border-emerald-300 text-emerald-900" : "bg-accent-100 border border-accent-300 text-accent-900"}`}>
          <i className={importResult.success ? "ri-check-line" : "ri-error-warning-line"}></i>
          {importResult.message}
          <button onClick={() => setImportResult(null)} className={`ml-auto font-medium cursor-pointer ${importResult.success ? "text-emerald-700" : "text-accent-700"}`}>Kapat</button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h4 className="text-lg font-semibold text-foreground-950"><a id="suppliers-title">Tedarikçiler</a></h4>
          <p className="text-sm text-foreground-500 mt-0.5">Tedarikçi firmaları yönetin</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={handleFileChange}
          />
          <button
            onClick={handleExcelImportClick}
            disabled={importing}
            className="flex items-center gap-2 px-4 py-2 bg-secondary-500 hover:bg-secondary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            {importing ? (
              <><i className="ri-loader-4-line animate-spin"></i> Aktarılıyor...</>
            ) : (
              <><i className="ri-file-excel-line"></i> Excel'den İçe Aktar</>
            )}
          </button>
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Tedarikçi ara..." className="pl-9 pr-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 w-full sm:w-52" />
          </div>
          <div className="flex items-center bg-background-100 rounded-lg p-0.5">
            <button onClick={() => setViewMode("grid")} className={`px-2.5 py-1.5 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${viewMode === "grid" ? "bg-background-50 text-foreground-950 font-medium" : "text-foreground-400 hover:text-foreground-600"}`}>
              <i className="ri-grid-line"></i>
            </button>
            <button onClick={() => setViewMode("list")} className={`px-2.5 py-1.5 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${viewMode === "list" ? "bg-background-50 text-foreground-950 font-medium" : "text-foreground-400 hover:text-foreground-600"}`}>
              <i className="ri-list-unordered"></i>
            </button>
          </div>
          <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-add-line"></i> Tedarikçi Ekle
          </button>
        </div>
      </div>

      {viewMode === "grid" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.length === 0 ? (
            <div className="col-span-full bg-background-50 border border-background-200/70 rounded-xl py-10 text-center text-sm text-foreground-400">
              <i className="ri-user-search-line text-2xl block mb-2"></i>
              Tedarikçi bulunamadı.
            </div>
          ) : (
            filtered.map((s) => (
              <div key={s.id} onClick={() => setDetailSupplier(s)} className="bg-background-50 border border-background-200/70 rounded-xl p-4 hover:border-background-300/60 transition-colors cursor-pointer">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <i className="ri-truck-line text-emerald-600"></i>
                    </div>
                    <div>
                      <p className="font-medium text-foreground-900 text-sm">{s.name}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={(e) => { e.stopPropagation(); setDetailSupplier(s); }} className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-emerald-600 hover:bg-emerald-100 cursor-pointer" title="Detay">
                      <i className="ri-eye-line text-sm"></i>
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); openEdit(s); }} className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-primary-600 hover:bg-primary-100 cursor-pointer" title="Düzenle"><i className="ri-edit-line text-sm"></i></button>
                    <button onClick={(e) => { e.stopPropagation(); deleteSupplier(s.id, s.name); }} className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-accent-500 hover:bg-accent-100 cursor-pointer" title="Sil"><i className="ri-delete-bin-line text-sm"></i></button>
                  </div>
                </div>
                <div className="space-y-1.5 text-sm">
                  <div className="flex items-center gap-2 text-foreground-500">
                    <i className="ri-phone-line text-xs"></i>
                    <span>{s.phone || "-"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-foreground-500">
                    <i className="ri-mail-line text-xs"></i>
                    <span>{s.email || "-"}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {viewMode === "list" && (
        <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[500px]">
              <thead>
                <tr className="bg-background-100 text-left">
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Firma</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">E-posta</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-right">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-10 text-center text-sm text-foreground-400">
                      <i className="ri-user-search-line text-2xl block mb-2"></i>
                      Tedarikçi bulunamadı.
                    </td>
                  </tr>
                ) : (
                  filtered.map((s) => (
                    <tr key={s.id} onClick={() => setDetailSupplier(s)} className="border-t border-background-200/70 hover:bg-background-100/50 transition-colors cursor-pointer">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-emerald-100 rounded-md flex items-center justify-center shrink-0">
                            <i className="ri-truck-line text-emerald-600 text-sm"></i>
                          </div>
                          <p className="font-medium text-foreground-900">{s.name}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-foreground-600">{s.phone || "-"}</td>
                      <td className="px-4 py-3 text-foreground-600">{s.email || "-"}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={(e) => { e.stopPropagation(); setDetailSupplier(s); }} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:text-emerald-600 hover:bg-emerald-100 cursor-pointer" title="Detay">
                            <i className="ri-eye-line"></i>
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); openEdit(s); }} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:text-primary-600 hover:bg-primary-100 cursor-pointer" title="Düzenle">
                            <i className="ri-edit-line"></i>
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); deleteSupplier(s.id, s.name); }} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:text-accent-500 hover:bg-accent-100 cursor-pointer" title="Sil">
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowModal(false)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-base font-semibold text-foreground-950"><a id="supplier-form">{editing ? "Tedarikçi Düzenle" : "Yeni Tedarikçi"}</a></h4>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer"><i className="ri-close-line"></i></button>
            </div>
            <div className="space-y-3">
              <div><label className="block text-xs font-medium text-foreground-500 mb-1">Firma Adı *</label><input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" /></div>
              <div><label className="block text-xs font-medium text-foreground-500 mb-1">Telefon</label><input type="text" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" /></div>
              <div><label className="block text-xs font-medium text-foreground-500 mb-1">E-posta</label><input type="text" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" /></div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setShowModal(false)} className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap">İptal</button>
              <button onClick={save} disabled={saving} className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap">
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Supplier Detail Modal */}
      {detailSupplier && (
        <SupplierDetailModal
          supplier={detailSupplier}
          dealerId={dealerId}
          onClose={() => { setDetailSupplier(null); setShowProductAdd(false); }}
          onAddProduct={() => setShowProductAdd(true)}
          onDataChanged={fetchSuppliers}
        />
      )}

      {/* Product Add Modal */}
      {showProductAdd && detailSupplier && (
        <SupplierProductAddModal
          dealerId={dealerId}
          supplierId={detailSupplier.id}
          supplierName={detailSupplier.name}
          onClose={() => setShowProductAdd(false)}
          onSuccess={() => {
            fetchSuppliers();
            setShowProductAdd(false);
          }}
        />
      )}
    </div>
  );
}